<template>
    <!-- the two teams side by side: one row per stat, a bar from the middle for each team, the better side in mint -->
    <div class="team-compare">
        <div class="team-compare__teams">
            <router-link v-for="(team, side) in teams" :key="side" :to="{ name: 'Team', params: { id: team.id } }" class="team-compare__team" :class="`team-compare__team--${side}`">
                <span class="cl-logo team-compare__logo">
                    <img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt="">
                    <v-icon v-else color="teal">mdi-basketball-hoop</v-icon>
                </span>
                <span class="team-compare__name">{{ team.name }}</span>
            </router-link>
        </div>

        <div v-for="row in rows" :key="row.key" class="team-compare__row">
            <div class="team-compare__side team-compare__side--left">
                <span class="team-compare__value" :class="{ 'team-compare__value--lead': row.lead === 1 }">
                    {{ row.display1 }}<small v-if="row.ratio1">{{ row.ratio1 }}</small>
                </span>
                <span class="team-compare__bar team-compare__bar--left">
                    <span class="team-compare__fill" :class="{ 'team-compare__fill--lead': row.lead === 1 }" :style="{ width: `${row.width1}%` }" />
                </span>
            </div>
            <span class="team-compare__label">{{ row.text }}</span>
            <div class="team-compare__side">
                <span class="team-compare__bar">
                    <span class="team-compare__fill" :class="{ 'team-compare__fill--lead': row.lead === 2 }" :style="{ width: `${row.width2}%` }" />
                </span>
                <span class="team-compare__value" :class="{ 'team-compare__value--lead': row.lead === 2 }">
                    {{ row.display2 }}<small v-if="row.ratio2">{{ row.ratio2 }}</small>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
    import { foulsOf } from '@/utils/teamStats'

    // the shots are shown as a percentage, with made / attempted under it
    const SHOTS = {
        two: ['twoPointsMade', 'twoPointAttempts'],
        three: ['threePointsMade', 'threePointAttempts'],
        ft: ['freeThrowsMade', 'freeThrowAttempts']
    }

    const ROWS = [
        { key: 'points', text: 'PTS' },
        { key: 'two', text: '2P%', shot: true },
        { key: 'three', text: '3P%', shot: true },
        { key: 'ft', text: 'FT%', shot: true },
        { key: 'offensiveRebounds', text: 'OR' },
        { key: 'defensiveRebounds', text: 'DR' },
        { key: 'rebounds', text: 'TR' },
        { key: 'assists', text: 'AST' },
        { key: 'steals', text: 'STL' },
        { key: 'turnovers', text: 'TO', lowerIsBetter: true },
        { key: 'blockedShots', text: 'BLK' },
        // every foul: personal, technical and unsportsmanlike
        { key: 'fouls', text: 'FC', lowerIsBetter: true }
    ]

    function totals(team) {
        const sum = {}
        ;((team && team.players) || []).forEach(player => {
            const stat = player.stats && player.stats[0]
            if (!stat) return
            Object.keys(stat).forEach(key => {
                if (typeof stat[key] === 'number') sum[key] = (sum[key] || 0) + stat[key]
            })
        })
        sum.fouls = foulsOf(sum)
        return sum
    }

    export default {
        props: {
            match: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            teams() {
                return [this.match.team1 || {}, this.match.team2 || {}]
            },
            rows() {
                const t1 = totals(this.match.team1)
                const t2 = totals(this.match.team2)
                return ROWS.map(row => {
                    let v1
                    let v2
                    const out = { key: row.key, text: row.text }
                    if (row.shot) {
                        const [made, att] = SHOTS[row.key]
                        v1 = t1[att] ? Math.round((t1[made] || 0) / t1[att] * 100) : 0
                        v2 = t2[att] ? Math.round((t2[made] || 0) / t2[att] * 100) : 0
                        out.display1 = `${v1}%`
                        out.display2 = `${v2}%`
                        out.ratio1 = `${t1[made] || 0}/${t1[att] || 0}`
                        out.ratio2 = `${t2[made] || 0}/${t2[att] || 0}`
                        out.width1 = v1
                        out.width2 = v2
                    } else {
                        v1 = t1[row.key] || 0
                        v2 = t2[row.key] || 0
                        out.display1 = v1
                        out.display2 = v2
                        const max = Math.max(v1, v2) || 1
                        out.width1 = v1 / max * 100
                        out.width2 = v2 / max * 100
                    }
                    out.lead = v1 === v2 ? 0 : ((v1 > v2) !== !!row.lowerIsBetter ? 1 : 2)
                    return out
                })
            }
        }
    }
</script>

<style scoped>
.team-compare {
    padding: 8px 22px 14px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 8px 24px rgba(35, 35, 35, 0.12);
    font-family: Roboto, Arial, sans-serif;
    font-variant-numeric: tabular-nums;
}

.team-compare__teams {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0 14px;
    margin-bottom: 4px;
    border-bottom: 2px solid rgba(3, 229, 183, 0.35);
}

.team-compare__team {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    color: #fff !important;
    text-decoration: none;
}

.team-compare__team--1 {
    flex-direction: row-reverse;
    text-align: right;
}

.team-compare__team:hover .team-compare__name {
    color: #03e5b7;
}

.team-compare__logo {
    width: 46px;
    height: 46px;
}

.team-compare__name {
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.35rem;
    line-height: 1.1;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    -webkit-line-clamp: 2;
}

.team-compare__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 64px minmax(0, 1fr);
    align-items: center;
    padding: 9px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.team-compare__row:last-child {
    border-bottom: 0;
}

.team-compare__side {
    display: flex;
    align-items: center;
    gap: 14px;
}

.team-compare__value {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 54px;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.1;
    color: rgba(255, 255, 255, 0.75);
}

.team-compare__value small {
    font-size: 0.68rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.45);
}

.team-compare__value--lead {
    color: #03e5b7;
}

.team-compare__bar {
    position: relative;
    flex: 1 1 auto;
    height: 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
}

.team-compare__fill {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.35);
    transition: width 0.4s;
}

/* the left bar grows from the middle towards the left */
.team-compare__bar--left .team-compare__fill {
    left: auto;
    right: 0;
}

.team-compare__fill--lead {
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 100%);
}

.team-compare__bar--left .team-compare__fill--lead {
    background: linear-gradient(270deg, #03e5b7 0%, #037ade 100%);
}

.team-compare__label {
    text-align: center;
    color: #03e5b7;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.08em;
}

@media (max-width: 600px) {
    .team-compare {
        padding: 4px 12px 10px;
    }

    .team-compare__logo {
        width: 34px;
        height: 34px;
    }

    .team-compare__name {
        font-size: 1.05rem;
    }

    .team-compare__row {
        grid-template-columns: minmax(0, 1fr) 42px minmax(0, 1fr);
    }

    .team-compare__side {
        gap: 8px;
    }

    .team-compare__value {
        min-width: 40px;
        font-size: 0.92rem;
    }

    .team-compare__label {
        font-size: 0.66rem;
    }
}
</style>
